import {
  ChatVisualization,
  parseVisualizationPayload,
} from './visualizationTypes';

export type { ChatVisualization } from './visualizationTypes';

export type ChatBlock =
  | { type: 'paragraph'; content: string }
  | { type: 'heading'; level: number; content: string }
  | { type: 'list'; items: string[] }
  | { type: 'visualization'; visualization: ChatVisualization };

const CODE_FENCE_PATTERN = /```[\w]*\s*([\s\S]*?)```/g;
const HEADING_PATTERN = /^(#{1,6})\s+(.+)$/;
const LIST_ITEM_PATTERN = /^([-*]|\d+\.)\s+(.+)$/;
const MARKDOWN_LINK_LINE_PATTERN = /^\[([^\]]+)\]\(([^)]+)\)\s*$/;
const INLINE_VISUALIZATION_PATTERN = /render_visuali[sz]ation\s*/gi;
const isMarkdownTable = (line: string) =>
    /^\s*\|.*\|\s*$/.test(line);

export const hasPendingVisualization = (text: string): boolean => {
  const matches = [...text.matchAll(INLINE_VISUALIZATION_PATTERN)];
  if (matches.length === 0) return false;

  for (const match of matches) {
    const matchIndex = match.index ?? 0;
    const jsonStart = text.indexOf('{', matchIndex + match[0].length);
    if (jsonStart === -1) return true;

    const jsonEnd = findBalancedJsonEnd(text, jsonStart);
    if (jsonEnd === -1) return true;

    const payload = text.slice(matchIndex, jsonEnd);
    if (!parseVisualizationPayload(payload)) return true;
  }

  return false;
};

const findBalancedJsonEnd = (text: string, jsonStart: number) => {
  let depth = 0;
  for (let index = jsonStart; index < text.length; index += 1) {
    const char = text[index];
    if (char === '{') depth += 1;
    if (char === '}') depth -= 1;
    if (depth === 0) return index + 1;
  }
  return -1;
};

const parseSectionWithVisualizations = (section: string): ChatBlock[] => {
  const blocks: ChatBlock[] = [];
  let lastIndex = 0;

  for (const match of section.matchAll(INLINE_VISUALIZATION_PATTERN)) {
    const matchIndex = match.index ?? 0;
    const before = section.slice(lastIndex, matchIndex).trim();
    if (before) {
      blocks.push(...parseMarkdownSection(before));
    }

    const jsonStart = section.indexOf('{', matchIndex + match[0].length);
    if (jsonStart === -1) {
      lastIndex = matchIndex + match[0].length;
      continue;
    }

    const jsonEnd = findBalancedJsonEnd(section, jsonStart);
    if (jsonEnd === -1) {
      lastIndex = matchIndex + match[0].length;
      continue;
    }

    const payload = section.slice(matchIndex, jsonEnd);
    const visualization = parseVisualizationPayload(payload);
    if (visualization) {
      blocks.push({ type: 'visualization', visualization });
    } else {
      blocks.push({ type: 'paragraph', content: payload });
    }

    lastIndex = jsonEnd;
  }

  const trailing = section.slice(lastIndex).trim();
  if (trailing) {
    blocks.push(...parseMarkdownSection(trailing));
  }

  return blocks;
};

const parseMarkdownSection = (section: string): ChatBlock[] => {
  const blocks: ChatBlock[] = [];
  const lines = section.split('\n');
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    const trimmedLine = line.trim();

    if (!trimmedLine) {
      index += 1;
      continue;
    }

    const headingMatch = trimmedLine.match(HEADING_PATTERN);
    if (headingMatch) {
      blocks.push({
        type: 'heading',
        level: headingMatch[1].length,
        content: headingMatch[2].trim(),
      });
      index += 1;
      continue;
    }

    const markdownLinkItems: string[] = [];
    while (index < lines.length) {
      const linkLine = lines[index].trim();
      if (!MARKDOWN_LINK_LINE_PATTERN.test(linkLine)) break;
      markdownLinkItems.push(linkLine);
      index += 1;
    }

    if (markdownLinkItems.length > 0) {
      blocks.push({ type: 'list', items: markdownLinkItems });
      continue;
    }

    const listItems: string[] = [];
    while (index < lines.length) {
      const listMatch = lines[index].trim().match(LIST_ITEM_PATTERN);
      if (!listMatch) break;
      listItems.push(listMatch[2].trim());
      index += 1;
    }

    if (listItems.length > 0) {
      blocks.push({ type: 'list', items: listItems });
      continue;
    }

    const paragraphLines: string[] = [trimmedLine];
    index += 1;
    while (index < lines.length) {
      const nextLine = lines[index].trim();
      if (
        !nextLine ||
        HEADING_PATTERN.test(nextLine) ||
        LIST_ITEM_PATTERN.test(nextLine) ||
        MARKDOWN_LINK_LINE_PATTERN.test(nextLine) ||
        isMarkdownTable(nextLine)
      ) {
        break;
      }
      paragraphLines.push(nextLine);
      index += 1;
    }

    blocks.push({ type: 'paragraph', content: paragraphLines.join('\n') });
  }

  return blocks;
};

export const parseChatMessage = (text: string): ChatBlock[] => {
  const blocks: ChatBlock[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(CODE_FENCE_PATTERN)) {
    const matchIndex = match.index ?? 0;
    const section = text.slice(lastIndex, matchIndex);
    if (section.trim()) {
      blocks.push(...parseSectionWithVisualizations(section));
    }

    const codeContent = match[1]?.trim() ?? '';
    const visualization = parseVisualizationPayload(codeContent);
    if (visualization) {
      blocks.push({ type: 'visualization', visualization });
    } else if (codeContent) {
      blocks.push({ type: 'paragraph', content: codeContent });
    }

    lastIndex = matchIndex + match[0].length;
  }

  const trailingSection = text.slice(lastIndex);
  if (trailingSection.trim()) {
    blocks.push(...parseSectionWithVisualizations(trailingSection));
  }

  if (blocks.length === 0 && text.trim()) {
    blocks.push(...parseSectionWithVisualizations(text));
  }

  return blocks;
};
